// @flow
import React, { useState, useEffect } from 'react';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Alert from '@mui/material/Alert';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import { useTheme } from '@mui/material/styles';
import { createClasses } from '../../../utils/stylesUtils';
import { TARGET_FIELDS, validateColumnMapping, formatNumber } from '../csvTemplateUtils';
import previewViewStyles from './PreviewView.Style';

type ParseResult = {
  totalRows: number,
  headers: Array<string>,
  sampleRows: Array<Object>,
  suggestedMapping: Object
};

type Props = {
  parseResult: ParseResult,
  onMappingConfirmed: (columnMapping: {[string]: string}) => void,
  onCancel: () => void
};

const PreviewView = ({ parseResult, onMappingConfirmed, onCancel }: Props) => {
  const theme = useTheme();
  const classes = createClasses(previewViewStyles, theme);

  const { totalRows, headers, sampleRows, suggestedMapping } = parseResult;

  // Initialize column mapping from suggested mapping
  // suggestedMapping is: { FirstName: 'First Name', LastName: 'Last Name', ... }
  // We need to convert to: { 'First Name': 'FirstName', 'Last Name': 'LastName', ... }
  const [columnMapping, setColumnMapping] = useState<{[string]: string}>(() => {
    const initialMapping = {};
    headers.forEach(header => {
      // Find if this header is in suggestedMapping values
      const targetField = Object.keys(suggestedMapping).find(
        key => suggestedMapping[key] === header
      );
      initialMapping[header] = targetField || '';
    });
    return initialMapping;
  });

  const [validationError, setValidationError] = useState<string | null>(null);

  // Re-validate when mapping changes
  useEffect(() => {
    const error = validateColumnMapping(columnMapping);
    setValidationError(error);
  }, [columnMapping]);

  const handleMappingChange = (csvColumn: string, targetField: string) => {
    // If selecting a target field, clear it from other columns first
    if (targetField) {
      const newMapping = { ...columnMapping };
      Object.keys(newMapping).forEach(key => {
        if (newMapping[key] === targetField && key !== csvColumn) {
          newMapping[key] = '';
        }
      });
      newMapping[csvColumn] = targetField;
      setColumnMapping(newMapping);
    } else {
      setColumnMapping(prev => ({
        ...prev,
        [csvColumn]: targetField
      }));
    }
  };

  const handleConfirm = () => {
    const error = validateColumnMapping(columnMapping);
    if (error) {
      setValidationError(error);
      return;
    }

    // Convert mapping to format expected by API
    // From: { 'First Name': 'FirstName', 'Last Name': 'LastName', ... }
    // To: { FirstName: 'First Name', LastName: 'Last Name', ... }
    const apiMapping: {[string]: string} = {};
    Object.keys(columnMapping).forEach(csvColumn => {
      const targetField = columnMapping[csvColumn];
      if (targetField) {
        apiMapping[targetField] = csvColumn;
      }
    });

    onMappingConfirmed(apiMapping);
  };

  const isFieldMapped = (targetField: string): boolean => {
    return Object.values(columnMapping).includes(targetField);
  };

  const getMappedCount = (): number => {
    return Object.values(columnMapping).filter(v => v !== '').length;
  };

  return (
    <Box sx={classes.container}>
      <Box sx={classes.header}>
        <Typography variant="h6" sx={classes.title}>
          Map Your Columns
        </Typography>
        <Box sx={classes.rowCount}>
          <CheckCircleIcon sx={classes.checkIcon} />
          <Typography variant="body1">
            <strong>{formatNumber(totalRows)}</strong> customers found
          </Typography>
        </Box>
      </Box>

      {validationError && (
        <Alert severity="error" sx={classes.alert}>
          {validationError}
        </Alert>
      )}

      <Box sx={classes.mappingSection}>
        <Typography variant="subtitle2" sx={classes.sectionTitle}>
          Column Mapping ({getMappedCount()} of {headers.length} mapped)
        </Typography>
        <Typography variant="body2" sx={classes.sectionSubtitle}>
          Map your CSV columns to customer fields. Phone and at least one name field are required.
        </Typography>

        <Box sx={classes.mappingGrid}>
          {headers.map(header => (
            <Box key={header} sx={classes.mappingRow}>
              <Typography variant="body2" sx={classes.csvColumn}>
                {header}
              </Typography>
              <Typography variant="body2" sx={classes.arrow}>
                →
              </Typography>
              <FormControl size="small" sx={classes.selectControl}>
                <Select
                  value={columnMapping[header] || ''}
                  onChange={(e) => handleMappingChange(header, e.target.value)}
                  displayEmpty
                  variant="outlined"
                  sx={classes.select}
                >
                  {TARGET_FIELDS.map(field => (
                    <MenuItem
                      key={field.value}
                      value={field.value}
                      disabled={field.value !== '' && isFieldMapped(field.value) && columnMapping[header] !== field.value}
                    >
                      {field.label}
                      {field.value === 'Phone' && ' (Required)'}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          ))}
        </Box>
      </Box>

      <Box sx={classes.previewSection}>
        <Typography variant="subtitle2" sx={classes.sectionTitle}>
          Sample Data Preview
        </Typography>
        <TableContainer sx={classes.tableContainer}>
          <Table size="small" sx={classes.table}>
            <TableHead>
              <TableRow>
                {headers.map(header => (
                  <TableCell key={header} sx={classes.tableHeaderCell}>
                    {header}
                    {columnMapping[header] && (
                      <Typography variant="caption" sx={classes.mappedLabel}>
                        → {TARGET_FIELDS.find(f => f.value === columnMapping[header])?.label}
                      </Typography>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sampleRows.slice(0, 5).map((row, index) => (
                <TableRow key={index}>
                  {headers.map(header => (
                    <TableCell key={header} sx={classes.tableCell}>
                      {row[header] || '-'}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Box sx={classes.actions}>
        <Button
          variant="outlined"
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={!!validationError}
        >
          Start Import
        </Button>
      </Box>
    </Box>
  );
};

export default PreviewView;
